import {
	Link,
	Outlet,
	useNavigate,
	useRouteContext,
} from "@tanstack/react-router";
import {
	ExternalLink,
	Images,
	LayoutDashboard,
	LogOut,
	Monitor,
	Puzzle,
	ShieldCheck,
	UserRound,
} from "lucide-react";
import { useEffect } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { flushAnalytics, track } from "@/lib/analytics";
import { authClient, invalidateSession } from "@/lib/auth-client";
import { getDesktopBridge } from "@/lib/desktop";
import { env } from "@/lib/env";
import { consumePendingReferral } from "@/lib/referral";
import { queryClient, trpc } from "@/lib/trpc";

const DESKTOP_RELEASES_URL = "https://www.pluck.so/download";
const EXTENSION_URL =
	"https://chromewebstore.google.com/detail/pluck/pdcikcbpfmbjcbccjopkplnegmnppcpc";

const navLinkClass =
	"flex items-center gap-2 rounded-full px-3 py-2 font-medium text-muted-foreground text-sm transition-colors hover:bg-secondary hover:text-foreground [&.active]:bg-accent [&.active]:font-semibold [&.active]:text-accent-foreground";

export function openExternal(url: string): void {
	const bridge = getDesktopBridge();
	if (bridge) {
		bridge.openExternal(url).catch(() => {
			/* ignore */
		});
		return;
	}
	window.open(url, "_blank", "noopener");
}

export function AppShell() {
	const { user, isAdmin } = useRouteContext({ from: "/app" });
	const navigate = useNavigate();

	// Attribute an invite code remembered before sign-in (covers OAuth and
	// magic-link round-trips that land here instead of on the login page).
	useEffect(() => {
		consumePendingReferral().then((attributed) => {
			if (attributed) {
				queryClient.invalidateQueries({
					queryKey: trpc.referral.getMyReferralInfo.queryKey(),
				});
			}
		});
	}, []);

	const signOut = async () => {
		track("auth.signed_out");
		await flushAnalytics();
		await authClient.signOut();
		invalidateSession();
		queryClient.clear();
		toast.success("Signed out");
		await navigate({ to: "/login", search: { redirect: undefined } });
	};

	return (
		<div className="flex h-full">
			<aside className="flex w-56 shrink-0 flex-col border-r bg-card">
				<div className="desktop-drag desktop-traffic-light-inset flex h-14 items-center gap-2 px-4 font-display font-extrabold text-[17px] tracking-[-0.01em]">
					<img alt="" className="size-6" src="/icon.png" />
					Pluck Studio
				</div>
				<nav aria-label="Main" className="flex flex-1 flex-col gap-1 px-2">
					<Link activeOptions={{ exact: true }} className={navLinkClass} to="/">
						<Images className="size-4" />
						Library
					</Link>
					<Link className={navLinkClass} to="/boards">
						<LayoutDashboard className="size-4" />
						Boards
					</Link>
					<button
						className={navLinkClass}
						onClick={() => {
							track("extension.get_clicked", { from: "sidebar" });
							openExternal(EXTENSION_URL);
						}}
						type="button"
					>
						<Puzzle className="size-4" />
						Get the extension
						<ExternalLink className="ml-auto size-3 opacity-60" />
					</button>
					<Link className={navLinkClass} to="/account">
						<UserRound className="size-4" />
						Account
					</Link>
					{!getDesktopBridge() && (
						<button
							className={navLinkClass}
							onClick={() => {
								track("desktop_app.get_clicked", { from: "sidebar" });
								openExternal(DESKTOP_RELEASES_URL);
							}}
							type="button"
						>
							<Monitor className="size-4" />
							Get the desktop app
							<ExternalLink className="ml-auto size-3 opacity-60" />
						</button>
					)}
					{isAdmin && (
						<button
							className={navLinkClass}
							onClick={() => {
								track("admin.opened", { from: "sidebar" });
								openExternal(`${env.VITE_WEB_URL}/admin`);
							}}
							type="button"
						>
							<ShieldCheck className="size-4" />
							Admin
							<ExternalLink className="ml-auto size-3 opacity-60" />
						</button>
					)}
				</nav>
				<div className="flex items-center gap-2 border-t p-3">
					<div className="min-w-0 flex-1">
						<p className="truncate font-medium text-sm">{user.name || "You"}</p>
						<p className="truncate text-muted-foreground text-xs">
							{user.email}
						</p>
					</div>
					<Button
						aria-label="Sign out"
						onClick={signOut}
						size="icon-sm"
						title="Sign out"
						variant="ghost"
					>
						<LogOut />
					</Button>
				</div>
			</aside>
			<main className="min-w-0 flex-1 overflow-auto">
				<Outlet />
			</main>
		</div>
	);
}
